import React, { useEffect, useState } from "react";
import { Eye, EyeOff, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PersonSearchSelect } from "@/components/common/PersonSearchSelect";
import { toast } from "sonner";

// Admin-only trigger that opens a dialog to preview the app as another role (read-only).
export function ViewAsControl() {
  const { isAdmin, isGroupAdmin, isManager, roles: myRoles, viewAs, startViewAs } = useAuth();
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [role, setRole] = useState("");
  const [district, setDistrict] = useState("");
  const [personId, setPersonIdRaw] = useState("");
  const [person, setPerson] = useState(null);
  const setPersonId = (p) => { const obj = typeof p === "string" ? (p ? { id: p } : null) : p; setPerson(obj); setPersonIdRaw(obj?.id || ""); };
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/view-as/roles").then((r) => setRoles(r.data.roles || [])).catch(() => setRoles([]));
    api.get("/organizations").then((r) => setOrgs(r.data.organizations || [])).catch(() => setOrgs([]));
  }, [open]);

  const canViewAs = isAdmin || isGroupAdmin || isManager || ["internal_recruiter", "recruiting_support"].some((r) => myRoles?.has?.(r));
  if (!canViewAs || viewAs) return null;
  const needsPerson = roles.find((r) => r.key === role)?.needs_person;
  const needsDistrict = roles.find((r) => r.key === role)?.needs_district;

  const go = async () => {
    if (!role) { toast.error("Select a role to preview"); return; }
    if (needsPerson && !personId) { toast.error("Select a preview person for this role"); return; }
    if (needsDistrict && !district) { toast.error("Select a district for this role"); return; }
    setBusy(true);
    try {
      await startViewAs({ role, district_org_id: district || null, person_id: personId || null });
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="view-as-trigger" aria-label="View As" title="View As"
        className="hidden sm:inline-flex gap-1.5">
        <Eye className="w-4 h-4" /> <span className="hidden 2xl:inline">View As</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="view-as-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-brand" /> View As / Log In As</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Choosing <b>Candidate</b> with a person logs you in as that Candidate so you can complete their portal on their behalf (your name is recorded on every change). Other roles open a read-only preview.</p>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs font-semibold text-slate-600">Role</label>
              <Select value={role} onValueChange={(v) => { setRole(v); setPersonId(null); }}>
                <SelectTrigger data-testid="view-as-role" className="mt-1"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {roles.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`view-as-role-${r.key}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">District / Organization {needsDistrict ? <span className="text-rose-500">*</span> : <span className="text-slate-400">(optional context)</span>}</label>
              <Select value={district || "none"} onValueChange={(v) => setDistrict(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="view-as-district" className="mt-1"><SelectValue placeholder="Any / account-wide" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  <SelectItem value="none">Any / account-wide</SelectItem>
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {needsPerson && (
              <div>
                <label className="text-xs font-semibold text-slate-600">Preview Person <span className="text-rose-500">*</span></label>
                <div className="mt-1" data-testid="view-as-person">
                  <PersonSearchSelect selected={person} onSelect={(p) => setPersonId(p)} />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={go} disabled={busy} data-testid="view-as-start" className="bg-brand hover:bg-brand-strong text-white">{busy ? "Starting…" : role === "candidate" ? "Log In As" : "Start Preview"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Persistent read-only banner shown whenever a View-As session is active.
export function ViewAsBanner() {
  const { viewAs, exitViewAs } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!viewAs) return null;
  const who = viewAs.person_name || viewAs.district_name;
  const loginAs = !!viewAs.login_as;
  const sub = [viewAs.role_label || viewAs.role, viewAs.person_name ? viewAs.district_name : null].filter(Boolean).join(" · ");
  const exit = async () => { setBusy(true); try { await exitViewAs(); } finally { setBusy(false); } };
  return (
    <div className="sticky top-0 z-50 flex items-center gap-3 bg-amber-500 text-white px-4 sm:px-6 py-2 shadow" data-testid="view-as-banner">
      <ShieldAlert className="w-5 h-5 shrink-0" />
      <div className="flex-1 min-w-0 text-sm leading-tight">
        <p><span className="font-bold uppercase tracking-wide" data-testid="view-as-viewed-user">{loginAs ? "Logged in as" : "Viewing as"} {who || viewAs.role_label || viewAs.role}</span>
          <span className="ml-2 text-amber-50/95">{sub}</span>{!loginAs && <span className="ml-2 font-semibold">· Read-Only Support View</span>}</p>
        {viewAs.admin_name && <p className="text-[11px] text-amber-50/90" data-testid="view-as-actor">Signed in as {viewAs.admin_name}</p>}
      </div>
      <Button size="sm" variant="secondary" onClick={exit} disabled={busy} data-testid="view-as-exit"
        className="gap-1.5 bg-white text-amber-700 hover:bg-amber-50">
        <EyeOff className="w-4 h-4" /> {busy ? "Exiting…" : loginAs ? "Exit Log In As" : "Exit View As"}
      </Button>
    </div>
  );
}
